import React, { useEffect } from 'react';
import { useCompanyBanks } from '../../lib/financeCatalogs';
import { useTranslation } from '../../lib/i18n';
import type { PaymentVia } from '../../lib/paymentVia';

export type PaymentViaValue = {
  paymentVia: PaymentVia | '';
  companyBankId: string;
  paidBy: string;
  /** Cash portion when via is split (also filled for pure cash). */
  cashAmount: string;
  /** Bank portion when via is online or split. */
  bankAmount: string;
};

type PaymentViaFieldsProps = {
  value: PaymentViaValue;
  onChange: (next: PaymentViaValue) => void;
  disabled?: boolean;
  /** disbursement = loan paid out; collection = EMI / receipt / sale */
  mode?: 'disbursement' | 'collection';
  /**
   * Expected total (principal, EMI, sale price, amount invested).
   * Used to auto-fill cash/bank and validate split.
   */
  totalAmount?: number;
};

function moneyStr(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(Math.round(n * 100) / 100);
}

function parseMoney(v: string): number {
  const n = parseFloat(String(v || '').replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function PaymentViaFields({
  value,
  onChange,
  disabled = false,
  mode = 'collection',
  totalAmount = 0,
}: PaymentViaFieldsProps) {
  const { t } = useTranslation();
  const banks = useCompanyBanks();
  const needsBank = value.paymentVia === 'online' || value.paymentVia === 'split';
  const total = Math.max(0, Number(totalAmount) || 0);

  // Keep pure cash / pure bank amounts in sync with the facility total.
  useEffect(() => {
    if (!value.paymentVia || total <= 0) return;
    if (value.paymentVia === 'cash') {
      const next = moneyStr(total);
      if (value.cashAmount !== next || value.bankAmount) {
        onChange({ ...value, cashAmount: next, bankAmount: '' });
      }
    } else if (value.paymentVia === 'online') {
      const next = moneyStr(total);
      if (value.bankAmount !== next || value.cashAmount) {
        onChange({ ...value, bankAmount: next, cashAmount: '' });
      }
    }
    // Intentionally omit onChange/value to avoid loops when parent recreates objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.paymentVia, total]);

  const viaLabel =
    mode === 'disbursement' ? t('payment.howWePaid') : t('payment.howTheyPaid');

  const setVia = (paymentVia: PaymentVia | '') => {
    if (paymentVia === 'cash') {
      onChange({
        paymentVia,
        companyBankId: '',
        paidBy: value.paidBy,
        cashAmount: total > 0 ? moneyStr(total) : value.cashAmount,
        bankAmount: '',
      });
      return;
    }
    if (paymentVia === 'online') {
      onChange({
        paymentVia,
        companyBankId: value.companyBankId,
        paidBy: value.paidBy,
        cashAmount: '',
        bankAmount: total > 0 ? moneyStr(total) : value.bankAmount,
      });
      return;
    }
    if (paymentVia === 'split') {
      const half = total > 0 ? moneyStr(Math.floor(total / 2)) : '';
      const rest = total > 0 ? moneyStr(total - parseMoney(half)) : '';
      onChange({
        paymentVia,
        companyBankId: value.companyBankId,
        paidBy: value.paidBy,
        cashAmount: value.cashAmount || half,
        bankAmount: value.bankAmount || rest,
      });
      return;
    }
    onChange({
      paymentVia: '',
      companyBankId: '',
      paidBy: value.paidBy,
      cashAmount: '',
      bankAmount: '',
    });
  };

  const splitCash = parseMoney(value.cashAmount);
  const splitBank = parseMoney(value.bankAmount);
  const splitSum = splitCash + splitBank;
  const splitDiff = total > 0 ? Math.round((splitSum - total) * 100) / 100 : 0;

  return (
    <div className="w-full min-w-0 space-y-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 sm:p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">{viaLabel}</p>

      <div className="w-full min-w-0">
        <label className="app-label">{t('payment.via')}</label>
        <select
          className="app-field box-border w-full min-w-0 max-w-full"
          value={value.paymentVia}
          onChange={(e) => setVia(e.target.value as PaymentVia | '')}
          disabled={disabled}
          required
        >
          <option value="">{t('payment.selectVia')}</option>
          <option value="cash">{t('payment.cash')}</option>
          <option value="online">{t('payment.online')}</option>
          <option value="split">{t('payment.split')}</option>
        </select>
      </div>

      {value.paymentVia === 'split' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="w-full min-w-0">
            <label className="app-label">{t('payment.cashAmount')}</label>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              className="app-field box-border w-full min-w-0 max-w-full"
              value={value.cashAmount}
              onChange={(e) => {
                const cashAmount = e.target.value;
                const cash = parseMoney(cashAmount);
                const bankAmount =
                  total > 0 && cash <= total ? moneyStr(Math.round((total - cash) * 100) / 100) : value.bankAmount;
                onChange({ ...value, cashAmount, bankAmount });
              }}
              disabled={disabled}
              required
              placeholder="0"
            />
          </div>
          <div className="w-full min-w-0">
            <label className="app-label">{t('payment.bankAmount')}</label>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              className="app-field box-border w-full min-w-0 max-w-full"
              value={value.bankAmount}
              onChange={(e) => {
                const bankAmount = e.target.value;
                const bank = parseMoney(bankAmount);
                const cashAmount =
                  total > 0 && bank <= total ? moneyStr(Math.round((total - bank) * 100) / 100) : value.cashAmount;
                onChange({ ...value, bankAmount, cashAmount });
              }}
              disabled={disabled}
              required
              placeholder="0"
            />
          </div>
          {total > 0 ? (
            <p
              className={`sm:col-span-2 text-xs ${
                Math.abs(splitDiff) > 0.5 ? 'font-medium text-rose-700' : 'text-zinc-500'
              }`}
            >
              {t('payment.splitTotalHint')
                .replace('{sum}', splitSum.toLocaleString('en-IN'))
                .replace('{total}', total.toLocaleString('en-IN'))}
            </p>
          ) : null}
        </div>
      ) : null}

      {needsBank ? (
        <>
          <div className="w-full min-w-0">
            <label className="app-label">{t('payment.selectBank')}</label>
            <select
              className="app-field box-border w-full min-w-0 max-w-full"
              value={value.companyBankId}
              onChange={(e) => onChange({ ...value, companyBankId: e.target.value })}
              disabled={disabled}
              required
            >
              <option value="">
                {banks.length ? t('payment.chooseBank') : t('payment.addBankInSettings')}
              </option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.bankName}
                  {b.branch ? ` — ${b.branch}` : ''}
                  {b.accountNumber ? ` (••••${b.accountNumber.slice(-4)})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full min-w-0">
            <label className="app-label">{t('payment.paidBy')}</label>
            <textarea
              className="app-field box-border w-full min-w-0 max-w-full resize-y"
              value={value.paidBy}
              onChange={(e) => onChange({ ...value, paidBy: e.target.value })}
              disabled={disabled}
              placeholder={t('payment.paidByPlaceholder')}
              rows={2}
            />
          </div>
        </>
      ) : value.paymentVia === 'cash' ? (
        <div className="w-full min-w-0">
          <label className="app-label">{t('payment.paidBy')} (optional)</label>
          <textarea
            className="app-field box-border w-full min-w-0 max-w-full resize-y"
            value={value.paidBy}
            onChange={(e) => onChange({ ...value, paidBy: e.target.value })}
            disabled={disabled}
            placeholder={t('payment.cashPaidByPlaceholder')}
            rows={2}
          />
        </div>
      ) : null}
    </div>
  );
}

export function validatePaymentVia(
  value: PaymentViaValue,
  mode: 'disbursement' | 'collection' = 'collection',
  totalAmount?: number
): string | null {
  if (!value.paymentVia) {
    return mode === 'disbursement'
      ? 'Select how the money was paid (cash, bank, or both).'
      : 'Select how the money was received (cash, bank, or both).';
  }
  if ((value.paymentVia === 'online' || value.paymentVia === 'split') && !value.companyBankId) {
    return 'Select the company bank account used for the bank portion.';
  }
  if (value.paymentVia === 'split') {
    const cash = parseMoney(value.cashAmount);
    const bank = parseMoney(value.bankAmount);
    if (cash <= 0 && bank <= 0) {
      return 'Enter how much was cash and how much was bank.';
    }
    if (cash <= 0 || bank <= 0) {
      return 'For cash + bank, both amounts must be greater than zero (or choose Cash or Bank alone).';
    }
    const total = Math.max(0, Number(totalAmount) || 0);
    if (total > 0 && Math.abs(cash + bank - total) > 0.5) {
      return `Cash + bank (₹${(cash + bank).toLocaleString('en-IN')}) must equal the total (₹${total.toLocaleString('en-IN')}).`;
    }
  }
  return null;
}

export function emptyPaymentVia(): PaymentViaValue {
  return { paymentVia: '', companyBankId: '', paidBy: '', cashAmount: '', bankAmount: '' };
}

/** Numeric cash/bank fields ready to persist on a payment or disbursement row. */
export function paymentViaPersistFields(value: PaymentViaValue, totalAmount: number) {
  const total = Math.max(0, Number(totalAmount) || 0);
  const via = value.paymentVia || undefined;
  if (via === 'online') {
    return {
      paymentVia: 'online' as const,
      companyBankId: value.companyBankId || undefined,
      paidBy: value.paidBy.trim() || undefined,
      cashAmount: 0,
      bankAmount: total,
    };
  }
  if (via === 'split') {
    return {
      paymentVia: 'split' as const,
      companyBankId: value.companyBankId || undefined,
      paidBy: value.paidBy.trim() || undefined,
      cashAmount: parseMoney(value.cashAmount),
      bankAmount: parseMoney(value.bankAmount),
    };
  }
  if (via === 'cash') {
    return {
      paymentVia: 'cash' as const,
      companyBankId: undefined as string | undefined,
      paidBy: value.paidBy.trim() || undefined,
      cashAmount: total,
      bankAmount: 0,
    };
  }
  return {
    paymentVia: undefined as undefined,
    companyBankId: undefined as string | undefined,
    paidBy: undefined as string | undefined,
    cashAmount: undefined as number | undefined,
    bankAmount: undefined as number | undefined,
  };
}
